import { useSendNotification } from "@app/hooks/useNotification";
import { clientFetch } from "@app/lib/egress/client";
import {
  getErrorFromResponse,
  useFetcher,
  useSWRWithDefaults,
} from "@app/lib/swr/swr";
import type { GetGranolaConnectionResponseBody } from "@app/types/api/me/granola";
import type { Result } from "@app/types/shared/result";
import { Err, Ok } from "@app/types/shared/result";
import { normalizeError } from "@app/types/shared/utils/error_utils";
import type { LightWorkspaceType } from "@app/types/user";
import { useCallback } from "react";
import type { Fetcher } from "swr";

function buildGranolaUrl(workspaceId: string): string {
  return `/api/w/${workspaceId}/me/granola`;
}

export function useGranolaConnection({
  owner,
  disabled,
}: {
  owner: LightWorkspaceType;
  disabled?: boolean;
}) {
  const { fetcher } = useFetcher();
  const granolaFetcher: Fetcher<GetGranolaConnectionResponseBody> = fetcher;

  const { data, error, mutate } = useSWRWithDefaults(
    buildGranolaUrl(owner.sId),
    granolaFetcher,
    { disabled }
  );

  return {
    granolaConnection: data?.connection ?? null,
    isGranolaConnectionLoading: !disabled && !error && !data,
    mutateGranolaConnection: mutate,
  };
}

export function useGranolaApiKeyActions({
  owner,
}: {
  owner: LightWorkspaceType;
}) {
  const sendNotification = useSendNotification();
  const { mutateGranolaConnection } = useGranolaConnection({
    owner,
    disabled: true,
  });

  const request = useCallback(
    async (
      method: "POST" | "DELETE",
      body?: unknown
    ): Promise<Result<void, Error>> => {
      try {
        const res = await clientFetch(buildGranolaUrl(owner.sId), {
          method,
          headers: body ? { "Content-Type": "application/json" } : undefined,
          body: body ? JSON.stringify(body) : undefined,
        });

        if (!res.ok) {
          const errorData = await getErrorFromResponse(res);
          sendNotification({
            type: "error",
            title: "Granola API key",
            description: errorData.message,
          });

          return new Err(new Error(errorData.message));
        }

        await mutateGranolaConnection();

        return new Ok(undefined);
      } catch (e) {
        const error = normalizeError(e);
        sendNotification({
          type: "error",
          title: "Granola API key",
          description: error.message,
        });

        return new Err(error);
      }
    },
    [mutateGranolaConnection, owner.sId, sendNotification]
  );

  const saveApiKey = useCallback(
    async (apiKey: string) => {
      const res = await request("POST", { apiKey });
      if (res.isOk()) {
        sendNotification({
          type: "success",
          title: "Granola connected",
          description:
            "Meeting notes will now reach Memoair as soon as Granola writes them.",
        });
      }

      return res;
    },
    [request, sendNotification]
  );

  const removeApiKey = useCallback(() => request("DELETE"), [request]);

  return { saveApiKey, removeApiKey };
}
